import React, { useState } from 'react';
import { Eye, EyeOff, GripVertical, Plus, Edit3, Trash2 } from 'lucide-react';
import { useTenantContext } from '../../context/TenantContext';
import { IconSelector } from './IconSelector';

interface AdminHeaderConfigProps {
  formData: any;
  handleConfigChange: (path: string, value: any) => void;
}

// Les icônes sont maintenant gérées par le composant IconSelector centralisé
// Voir src/components/admin/IconSelector.tsx pour modifier la liste

const defaultMenuItems = [
  { id: 'blog', name: 'Blog', enabled: true, icon: 'FileText', isCustom: false },
  { id: 'portfolio', name: 'Portfolio', enabled: true, icon: 'Image', isCustom: false },
  { id: 'boutique', name: 'Boutique', enabled: true, icon: 'ShoppingBag', isCustom: false },
  { id: 'elearning', name: 'E-Learning', enabled: false, icon: 'BookOpen', isCustom: false },
  { id: 'santepsy', name: 'Santé Psy', enabled: false, icon: 'Stethoscope', isCustom: false },
  { id: 'partners', name: 'Partenaires', enabled: true, icon: 'Building', isCustom: false },
  { id: 'messages', name: 'Messages', enabled: true, icon: 'MessageSquare', isCustom: false },
  { id: 'crowdfunding', name: 'Soutenir', enabled: false, icon: 'HeartHandshake', isCustom: false },
  { id: 'mediatheque', name: 'Médiathèque', enabled: false, icon: 'Image', isCustom: false },
  { id: 'clients', name: 'Clients', enabled: false, icon: 'Users', isCustom: false },
  { id: 'configuration', name: 'Configuration', enabled: false, icon: 'Settings', isCustom: false },
  { id: 'dashboard', name: 'Tableau de bord', enabled: false, icon: 'LayoutDashboard', isCustom: false },
];

const AdminHeaderConfig: React.FC<AdminHeaderConfigProps> = ({ formData, handleConfigChange }) => {
  const { setPreviewValue } = useTenantContext();

  // Couleurs header admin
  const handleHeaderColorChange = (key: 'textColor' | 'backgroundColor' | 'iconColor', value: string) => {
    handleConfigChange(`adminHeader.${key}`, value);
    setPreviewValue(`adminHeader.${key}`, value);
  };

  // Menus
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverItem, setDragOverItem] = useState<string | null>(null);
  const [newMenuItem, setNewMenuItem] = useState({ name: '', icon: 'FileText', href: '', enabled: true });

  const menuItems = formData.config.adminHeader?.menuItems || defaultMenuItems;

  const handleMenuItemToggle = (itemId: string) => {
    const updatedItems = menuItems.map((item: any) => item.id === itemId ? { ...item, enabled: !item.enabled } : item);
    handleConfigChange('adminHeader.menuItems', updatedItems);
    setPreviewValue('adminHeader.menuItems', updatedItems);
  };

  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    setDraggedItem(itemId);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e: React.DragEvent, itemId: string) => { e.preventDefault(); setDragOverItem(itemId); };
  const handleDragLeave = () => setDragOverItem(null);
  const handleDrop = (e: React.DragEvent, targetItemId: string) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetItemId) return;
    const draggedIndex = menuItems.findIndex((i: any) => i.id === draggedItem);
    const targetIndex = menuItems.findIndex((i: any) => i.id === targetItemId);
    if (draggedIndex === -1 || targetIndex === -1) return;
    const newItems = [...menuItems];
    const [dragged] = newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, dragged);
    handleConfigChange('adminHeader.menuItems', [...newItems]);
    setPreviewValue('adminHeader.menuItems', [...newItems]);
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleAddMenuItem = () => {
    if (!newMenuItem.name.trim()) return;
    const newId = `custom_${Date.now()}`;
    const newItem = { id: newId, name: newMenuItem.name.trim(), icon: newMenuItem.icon, href: newMenuItem.href || `/admin/${newId}`, enabled: newMenuItem.enabled, isCustom: true };
    const updatedItems = [...menuItems, newItem];
    handleConfigChange('adminHeader.menuItems', updatedItems);
    setPreviewValue('adminHeader.menuItems', updatedItems);
    setNewMenuItem({ name: '', icon: 'FileText', href: '', enabled: true });
    setShowAddForm(false);
  };

  const handleDeleteMenuItem = (itemId: string) => {
    const updatedItems = menuItems.filter((item: any) => item.id !== itemId);
    handleConfigChange('adminHeader.menuItems', updatedItems);
    setPreviewValue('adminHeader.menuItems', updatedItems);
  };

  const handleEditMenuItem = (itemId: string, updates: any) => {
    const updatedItems = menuItems.map((item: any) => item.id === itemId ? { ...item, ...updates } : item);
    handleConfigChange('adminHeader.menuItems', updatedItems);
    setPreviewValue('adminHeader.menuItems', updatedItems);
    setEditingItem(null);
  };

  return (
    <div className="space-y-8">
      {/* Couleurs header admin */}
      <div>
        <h3 className="text-lg font-medium text-white mb-6 border-b border-white/10 pb-2">Couleurs du header admin</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Couleur du texte
              <span className="text-gray-500 text-xs block">(tenant.config.adminHeader.textColor)</span>
            </label>
            <div className="flex space-x-2">
              <input type="color" value={formData.config.adminHeader?.textColor || '#ffffff'} onChange={(e) => handleHeaderColorChange('textColor', e.target.value)} className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
              <input type="text" value={formData.config.adminHeader?.textColor || '#ffffff'} onChange={(e) => handleHeaderColorChange('textColor', e.target.value)} className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Couleur du background
              <span className="text-gray-500 text-xs block">(tenant.config.adminHeader.backgroundColor)</span>
            </label>
            <div className="flex space-x-2">
              <input type="color" value={formData.config.adminHeader?.backgroundColor || '#002366'} onChange={(e) => handleHeaderColorChange('backgroundColor', e.target.value)} className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
              <input type="text" value={formData.config.adminHeader?.backgroundColor || '#002366'} onChange={(e) => handleHeaderColorChange('backgroundColor', e.target.value)} className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Couleur des icônes
              <span className="text-gray-500 text-xs block">(tenant.config.adminHeader.iconColor)</span>
            </label>
            <div className="flex space-x-2">
              <input type="color" value={formData.config.adminHeader?.iconColor || '#D4AF37'} onChange={(e) => handleHeaderColorChange('iconColor', e.target.value)} className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
              <input type="text" value={formData.config.adminHeader?.iconColor || '#D4AF37'} onChange={(e) => handleHeaderColorChange('iconColor', e.target.value)} className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors" />
            </div>
          </div>
        </div>
      </div>

      {/* Gestion des menus */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-white border-b border-white/10 pb-2">Gestion des menus</h3>
            <p className="text-gray-400 text-sm mt-2">Activez/désactivez les menus, réorganisez-les par glisser-déposer, ajoutez ou supprimez des éléments</p>
          </div>
          <button onClick={() => setShowAddForm(true)} className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors">
            <Plus className="h-4 w-4" />
            <span>Ajouter un menu</span>
          </button>
        </div>

        {showAddForm && (
          <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
            <h4 className="text-white font-medium mb-4">Ajouter un nouveau menu</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Nom du menu</label>
                <input type="text" value={newMenuItem.name} onChange={(e) => setNewMenuItem({ ...newMenuItem, name: e.target.value })} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" placeholder="Ex: Mon Menu" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">URL (optionnel)</label>
                <input type="text" value={newMenuItem.href} onChange={(e) => setNewMenuItem({ ...newMenuItem, href: e.target.value })} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500" placeholder="Ex: /admin/mon-menu" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Icône</label>
                <IconSelector
                  value={newMenuItem.icon}
                  onChange={(value) => setNewMenuItem({ ...newMenuItem, icon: value })}
                  preset="all"
                />
              </div>
              <div className="flex items-center space-x-3">
                <input type="checkbox" id="new-menu-enabled" checked={newMenuItem.enabled} onChange={(e) => setNewMenuItem({ ...newMenuItem, enabled: e.target.checked })} className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
                <label htmlFor="new-menu-enabled" className="text-gray-200 cursor-pointer">Activé par défaut</label>
              </div>
            </div>
            <div className="flex space-x-3 mt-4">
              <button onClick={handleAddMenuItem} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors">Ajouter</button>
              <button onClick={() => { setShowAddForm(false); setNewMenuItem({ name: '', icon: 'FileText', href: '', enabled: true }); }} className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md transition-colors">Annuler</button>
            </div>
          </div>
        )}

        <div className="space-y-2" style={{ minHeight: '20px' }}>
          {menuItems.map((item: any) => (
            <div
              key={item.id}
              className={`flex items-center space-x-3 p-3 bg-white/5 rounded-lg border border-white/10 transition-all ${draggedItem === item.id ? 'shadow-lg bg-white/10 scale-105 opacity-50' : ''} ${dragOverItem === item.id ? 'border-green-400/50 bg-green-500/10' : ''}`}
              style={{ userSelect: 'none' }}
              draggable
              onDragStart={(e) => handleDragStart(e, item.id)}
              onDragOver={(e) => handleDragOver(e, item.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, item.id)}
            >
              <div className="text-gray-400 hover:text-white cursor-grab active:cursor-grabbing flex-shrink-0" style={{ touchAction: 'none' }}>
                <GripVertical className="h-5 w-5" />
              </div>

              <button onClick={() => handleMenuItemToggle(item.id)} className={`p-1 rounded transition-colors ${item.enabled ? 'text-green-400 hover:text-green-300' : 'text-gray-500 hover:text-gray-400'}`}>
                {item.enabled ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
              </button>

              <div className="flex-1 flex items-center space-x-3">
                {editingItem === item.id ? (
                  <div className="flex items-center space-x-2 flex-1">
                    <input type="text" defaultValue={item.name} onBlur={(e) => handleEditMenuItem(item.id, { name: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') handleEditMenuItem(item.id, { name: (e.currentTarget as HTMLInputElement).value }); if (e.key === 'Escape') setEditingItem(null); }} className="px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-gray-400" autoFocus />
                    <div className="w-48">
                      <IconSelector
                        value={item.icon}
                        onChange={(value) => handleEditMenuItem(item.id, { icon: value })}
                        preset="all"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <span className={`font-medium ${item.enabled ? 'text-white' : 'text-gray-500'}`}>{item.name}</span>
                    <span className="text-xs text-gray-500">({item.icon})</span>
                  </>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <div className={`px-2 py-1 rounded text-xs ${item.enabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-500'}`}>
                  {item.enabled ? 'Activé' : 'Désactivé'}
                </div>
                <div className="flex items-center space-x-1">
                  {item.isCustom && (
                    <>
                      <button onClick={() => setEditingItem(editingItem === item.id ? null : item.id)} className="p-1 text-gray-400 hover:text-blue-400 transition-colors" title="Modifier">
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDeleteMenuItem(item.id)} className="p-1 text-gray-400 hover:text-red-400 transition-colors" title="Supprimer">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  {!item.isCustom && (
                    <span className="text-xs text-gray-500 px-2 py-1 bg-blue-500/20 text-blue-400 rounded">Système</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminHeaderConfig;
